import React, { useEffect, useState } from 'react';
import { Download, Loader2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg, eur, downloadFile } from '../api';
import { usePortal } from '../PortalAuth';
import { usePrices } from '../PurchaseDialog';
import { Card, SectionTitle, Spinner } from '../ui';

function Row({ item, kind, label, sub, owned, price, onBuy, busy }) {
  return (
    <div className="py-3 flex items-center gap-4" data-testid={`doc-row-${item.imageId}`}>
      <div className="flex-1 min-w-0"><p className="text-sm">{label}</p><p className="text-xs text-[#5A5F67] font-mono truncate">{sub}</p></div>
      {owned ? (
        <button onClick={() => downloadFile(`/cs/my-documents/${owned}/file`, `${kind}.${kind === 'visura' ? 'pdf' : 'zip'}`).catch((e) => toast.error(errMsg(e)))} className="text-[#D4AF37] text-xs flex items-center gap-1" data-testid={`doc-owned-${item.imageId}`}><Download size={14} />Scarica</button>
      ) : (
        <button disabled={busy} onClick={onBuy} className="text-xs border border-[#1F242C] hover:border-[#D4AF37] px-3 h-8 flex items-center gap-2 rounded-sm" data-testid={`doc-buy-${item.imageId}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />} {eur(price)}
        </button>
      )}
    </div>
  );
}

export default function DocumentsPanel({ companyId, companyName }) {
  const prices = usePrices();
  const { refresh } = usePortal();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/cs/documents/${companyId}`).then((r) => setData(r.data)).catch((e) => setData({ visure: [], bilanci: [], errors: [errMsg(e)] }));
  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async (item, kind, label) => {
    setBusy(item.imageId);
    try {
      const { data: d } = await api.post('/cs/documents/purchase', { company_id: companyId, image_id: item.imageId, kind, company_name: companyName, label });
      toast.success('Documento acquistato');
      refresh(); await load();
      await downloadFile(`/cs/my-documents/${d.id}/file`, d.filename);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) return <Spinner />;
  const owned = data.owned || {};
  const bilanci = data.bilanci.filter((b) => ['AnnualAccounts', 'OpticalAnnualAccounts', 'OpticalConsolidatedAnnualAccounts'].includes(b.document?.imageType) || true);

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-6">
        <SectionTitle>Visure camerali</SectionTitle>
        {data.visure.length === 0 && <p className="text-sm text-[#5A5F67]">Nessuna visura disponibile.</p>}
        <div className="divide-y divide-[#1F242C]">
          {data.visure.map((v) => (
            <Row key={v.imageId} item={v} kind="visura" label={v.document?.imageTypeDescription || v.document?.imageTypeCode} sub={v.imageId}
              owned={owned[v.imageId]} price={prices?.visura?.cents} busy={busy === v.imageId} onBuy={() => buy(v, 'visura', v.document?.imageTypeDescription)} />
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <SectionTitle>Bilanci depositati</SectionTitle>
        {bilanci.length === 0 && <p className="text-sm text-[#5A5F67]">Nessun bilancio disponibile.</p>}
        <div className="divide-y divide-[#1F242C]">
          {bilanci.map((b) => {
            const label = `Bilancio ${(b.filingDate || '').slice(0, 4)}${b.consolidated ? ' consolidato' : ''} · ${b.document?.imageType || ''}`;
            return <Row key={b.imageId} item={b} kind="bilancio" label={label} sub={b.imageId} owned={owned[b.imageId]} price={prices?.bilancio?.cents} busy={busy === b.imageId} onBuy={() => buy(b, 'bilancio', label)} />;
          })}
        </div>
      </Card>
      {data.errors?.length > 0 && <p className="text-xs text-[#7A7F87] lg:col-span-2">{data.errors.join(' · ')}</p>}
    </div>
  );
}
